import React, { useState, useRef } from "react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { useCollections } from "@/contexts/collections";
import { useQueryClient } from "@tanstack/react-query";
import { importBookmarksFromHtml } from "@/utils/htmlBookmarkUtils";
import { Label } from "@/components/ui/label";
import { FileUp } from "lucide-react";
import { Tier1Tracking } from "@/services/analytics/Tier1Tracking";

interface ImportBookmarksDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const ImportBookmarksDialog: React.FC<ImportBookmarksDialogProps> = ({ open, onOpenChange }) => {
  const { user } = useAuth();
  const { refreshCollections } = useCollections();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const [isImporting, setIsImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [progressStage, setProgressStage] = useState("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [importError, setImportError] = useState<string | null>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setSelectedFile(e.target.files[0]);
      setImportError(null); // Clear any previous errors
    }
  };

  const handleCancel = () => {
    setSelectedFile(null);
    setImportError(null);
    onOpenChange(false);
  };

  const handleImport = async () => {
    if (!user || !selectedFile) return;
    
    setIsImporting(true);
    setImportProgress(0);
    setProgressStage("Starting import...");
    setImportError(null);
    
    // Track bookmark import start - using 'import_process' category
    Tier1Tracking.trackEvent(
      'bookmark_import_started',
      'import_process',
      { file_name: selectedFile.name, file_size: selectedFile.size },
      user
    );
    
    try {
      console.log(`Starting import of file: ${selectedFile.name} (${selectedFile.type}, ${selectedFile.size} bytes)`);
      
      // Read the file content
      const fileContent = await readFileAsText(selectedFile);
      console.log(`File content read successfully (${fileContent.length} characters)`);
      
      // Process the import with improved logging
      const result = await importBookmarksFromHtml(
        fileContent,
        user.id,
        (stage, progress) => {
          console.log(`Import progress: ${stage} - ${progress}%`);
          setProgressStage(stage);
          setImportProgress(progress);
        }
      );
      
      if (result.success) {
        console.log(`Import completed successfully: ${result.collectionsCreated} collections, ${result.bookmarksImported} bookmarks`);
        
        // Track bookmark import completion - using 'import_process' category
        Tier1Tracking.trackEvent(
          'bookmark_import_completed',
          'import_process',
          { 
            collections_created: result.collectionsCreated,
            bookmarks_imported: result.bookmarksImported,
            import_duration_ms: Date.now()
          },
          user
        );
        
        toast.success(
          `Import successful: Created ${result.collectionsCreated} collections with ${result.bookmarksImported} bookmarks`
        );
        
        // Refresh data
        refreshCollections();
        queryClient.invalidateQueries({ queryKey: ['contentItems'] });
        
        // Close dialog
        onOpenChange(false);
      } else {
        console.error(`Import failed: ${result.error || "Unknown error"}`);
        setImportError(result.error || "Unknown error occurred during import");
        toast.error(`Import failed: ${result.error || "Unknown error"}`);
      }
    } catch (error: any) {
      console.error("Error during import:", error);
      setImportError(error.message || "An unexpected error occurred");
      toast.error(`Import error: ${error.message}`);
    } finally {
      setIsImporting(false);
    }
  };

  const readFileAsText = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (event) => {
        if (event.target?.result) {
          resolve(event.target.result as string);
        } else {
          reject(new Error("Failed to read file"));
        }
      };
      reader.onerror = (event) => {
        console.error("FileReader error:", event);
        reject(new Error("File read error"));
      };
      reader.readAsText(file);
    });
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => {
      if (!isImporting) onOpenChange(isOpen); // Only allow closing if not importing
    }}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Import Bookmarks from HTML</DialogTitle>
        </DialogHeader>
        
        <div className="mt-4 space-y-4">
          {!isImporting ? (
            <>
              <div className="flex flex-col space-y-2">
                <Label htmlFor="bookmark-file" className="text-sm font-medium">
                  Select HTML Bookmarks File
                </Label>
                <p className="text-sm text-muted-foreground mb-2">
                  Upload an HTML file exported from your browser's bookmarks manager.
                  Your bookmark folders will be imported as collections.
                </p>
                
                <div className="flex items-center justify-center w-full">
                  <label 
                    htmlFor="bookmark-file" 
                    className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100"
                  >
                    <div className="flex flex-col items-center justify-center pt-5 pb-6">
                      <FileUp className="w-8 h-8 mb-3 text-gray-400" />
                      <p className="mb-2 text-sm text-gray-500">
                        <span className="font-semibold">Click to upload</span> or drag and drop
                      </p>
                      <p className="text-xs text-gray-500">HTML bookmark file (.html)</p>
                    </div>
                    <input
                      id="bookmark-file"
                      ref={fileInputRef}
                      type="file"
                      accept=".html,.htm"
                      className="hidden"
                      onChange={handleFileChange}
                    />
                  </label>
                </div>
                
                {selectedFile && (
                  <p className="text-sm text-center mt-2">
                    Selected: <span className="font-medium">{selectedFile.name}</span>
                  </p>
                )}
                
                {importError && (
                  <div className="p-3 mt-2 bg-red-50 border border-red-200 rounded-md">
                    <p className="text-sm text-red-600">{importError}</p>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="space-y-4">
              <div className="text-center">
                <p className="text-sm font-medium mb-2">{progressStage}</p>
                <Progress value={importProgress} className="h-2 w-full" />
                <p className="text-xs text-muted-foreground mt-1">
                  {importProgress}% complete
                </p>
              </div>
            </div>
          )}
        </div>
        
        <DialogFooter>
          <Button 
            variant="outline" 
            onClick={handleCancel}
            disabled={isImporting}
          >
            {isImporting ? "Please wait..." : "Cancel"}
          </Button>
          <Button 
            type="submit" 
            onClick={handleImport}
            disabled={!selectedFile || isImporting}
          >
            {isImporting ? "Importing..." : "Import"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ImportBookmarksDialog;
